import React, { useEffect, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { useLanguage } from '../contexts/LanguageContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ShopRedirectPage = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { t } = useLanguage();
  const [status, setStatus] = useState('loading'); // loading | notfound | error
  const [brand, setBrand] = useState('');

  useEffect(() => {
    let cancelled = false;
    const go = async () => {
      try {
        const res = await axios.get(`${API}/shop/${slug}/link`);
        if (cancelled) return;
        setBrand(res.data.brand);

        // Preferred: internal redirect to the category page with the
        // brand highlighted (keeps social-media traffic inside U&Fashion).
        if (res.data.category_slug) {
          const brandSlug = res.data.brand_slug || slug;
          navigate(
            `/category/${res.data.category_slug}?highlight=${encodeURIComponent(brandSlug)}`,
            { replace: true }
          );
          return;
        }

        // Legacy fallback: if for any reason the backend couldn't resolve
        // a category, still send the visitor to the affiliate URL.
        if (res.data.affiliate_link) {
          window.location.replace(res.data.affiliate_link);
        }
      } catch (err) {
        if (cancelled) return;
        setStatus(err?.response?.status === 404 ? 'notfound' : 'error');
      }
    };
    go();
    return () => {
      cancelled = true;
    };
  }, [slug, navigate]);

  return (
    <div
      className="min-h-screen bg-gradient-to-br from-pink-50 via-purple-50/40 to-indigo-50/40 flex items-center justify-center px-4"
      data-testid="shop-redirect-page"
    >
      <div className="max-w-md w-full bg-white rounded-2xl shadow-sm p-10 text-center">
        {status === 'loading' && (
          <>
            <div className="w-10 h-10 border-2 border-purple-500 border-t-transparent rounded-full animate-spin mx-auto mb-5" />
            <h1 className="text-xl font-medium text-gray-900 mb-2">
              {brand
                ? t(`Een ogenblik — we laten je ${brand} zien…`, `One moment — showing you ${brand}…`)
                : t('Een ogenblik…', 'One moment…')}
            </h1>
            <p className="text-sm text-gray-500">
              {t(
                'We brengen je naar de juiste collectie op U&Fashion.',
                'Taking you to the right collection on U&Fashion.'
              )}
            </p>
          </>
        )}
        {status === 'notfound' && (
          <>
            <h1 className="text-2xl font-light text-gray-900 mb-3">
              {t('Merk niet gevonden', 'Brand not found')}
            </h1>
            <p className="text-gray-600 mb-6">
              {t(
                'De link werkt niet meer of het merk is van onze selectie verwijderd.',
                'This link no longer works or the brand has been removed from our selection.'
              )}
            </p>
            <Link
              to="/"
              className="inline-block bg-gray-900 text-white px-6 py-3 rounded-full text-sm font-medium hover:bg-gray-800 transition-colors"
              data-testid="shop-redirect-home-link"
            >
              {t('Terug naar home', 'Back to home')}
            </Link>
          </>
        )}
        {status === 'error' && (
          <>
            <h1 className="text-xl font-medium text-gray-900 mb-3">
              {t('Er ging iets mis', 'Something went wrong')}
            </h1>
            <p className="text-gray-600 mb-6">
              {t('Probeer het later opnieuw.', 'Please try again later.')}
            </p>
            <Link
              to="/"
              className="inline-block bg-gray-900 text-white px-6 py-3 rounded-full text-sm font-medium hover:bg-gray-800 transition-colors"
              data-testid="shop-redirect-error-home-link"
            >
              {t('Terug naar home', 'Back to home')}
            </Link>
          </>
        )}
      </div>
    </div>
  );
};

export default ShopRedirectPage;
